/**
 * Shared work-order customer logic — one applicant with their services, custom field values and
 * documents. Mirrors the per-customer behaviour of the Create Work Order page so the "Add customer"
 * flow on the work order detail page builds exactly the same line items, uploads documents the same
 * way, and runs the same Thaklis employee tracking. (The create page still has its own copy; it is
 * meant to move onto these helpers later.)
 */
import ApiService from '../../services/api.service';
import { submitWorkOrderDocuments, needsEmployeeSync } from './documentPrefill';

export const VAT_RATE = 0.05;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A blank applicant. */
export const createCustomer = () => ({
  id: Date.now(),
  name: '',
  passportNumber: '',
  emiratesId: '',
  // Optional contact details — sent to Thaklis with the employee, not stored in Managers.
  phone: '',
  email: '',
  services: [],
  documents: [],
  // The Thaklis employee picked (or Quick-added) for this applicant when employee selection is on.
  employee: null,
  // Document types added from "Add document" (optional rows), and the rows already pre-filled (or tried).
  extraDocTypes: [],
  _prefillTried: [],
  // Custom field values shared across this customer's services, keyed by fieldId.
  fieldValues: {}
});

/** Custom fields of all the customer's services, deduped by fieldId (required if any service requires it). */
export const mergeCustomFields = (customer) => {
  const map = new Map();
  (customer?.services || []).forEach(svc => {
    (svc.customFields || []).forEach(f => {
      const key = f.fieldId || f.code;
      if (!key) return;
      const existing = map.get(key);
      if (existing) {
        if (f.required) existing.required = true;
      } else {
        map.set(key, { ...f, fieldId: f.fieldId || f.code, required: !!f.required });
      }
    });
  });
  return Array.from(map.values());
};

/** Required-document rules of all the customer's services, deduped by code (required if any requires it). */
export const mergeRequiredDocs = (customer) => {
  const map = new Map();
  (customer?.services || []).forEach(svc => {
    (svc.documentRequirements || []).forEach(r => {
      const key = r.code;
      if (!key) return;
      const existing = map.get(key);
      if (existing) {
        if (r.required) existing.required = true;
      } else {
        map.set(key, { ...r, required: !!r.required });
      }
    });
  });
  return Array.from(map.values());
};

/** Normalise a service from internalServiceService.getServicesForLineItems into the picker shape. */
export const toSelectableService = (s) => ({
  id: s.id,
  name: s.name || s.service_name,
  description: s.description || '',
  service_charge: s.service_charge || s.serviceCharge || 0,
  tax_on_service_charge: s.tax_on_service_charge || s.taxOnServiceCharge || 0,
  govt_charge: s.govt_charge || s.govtCharge || 0,
  type: s.type || 'THAKLIS',
  createsTask: s.createsTask || s.creates_task || false,
  customFields: s.customFields || s.custom_fields || [],
  documentRequirements: s.documentRequirements || s.document_requirements || []
});

/** A staged service row for the customer, from a picked service + a VAT-inclusive markup. */
export const buildServiceRow = (service, markupInclVat) => {
  const serviceCharge = service.service_charge || 0;
  const serviceTax = service.tax_on_service_charge || 0;
  const govtCharge = service.govt_charge || 0;
  const markupIncl = parseFloat(markupInclVat) || 0;
  const markupBase = Number((markupIncl / 1.05).toFixed(2));
  const markupVat = Number((markupIncl - markupBase).toFixed(2));
  return {
    id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    service_id: service.id,
    service_name: service.name,
    service_description: service.description || '',
    quantity: 1,
    unit_price: serviceCharge + govtCharge,
    service_charge: serviceCharge,
    govt_charge: govtCharge,
    tax_value: serviceTax,
    tax_on_markup: markupVat,
    line_total: serviceCharge + govtCharge + markupBase + serviceTax + markupVat,
    original_line_total: serviceCharge + govtCharge + serviceTax,
    markup_amount: markupBase,
    is_custom_service: service.type === 'INTERNAL',
    service_type: service.type || 'THAKLIS',
    creates_task: service.createsTask || false,
    documentRequirements: service.documentRequirements || service.document_requirements || [],
    customFields: (service.customFields || service.custom_fields || []).map(f => ({
      fieldId: f.fieldId || f.field_id || f.code,
      code: f.code,
      label: f.label,
      fieldType: f.fieldType || f.field_type || 'TEXT',
      required: f.required,
      options: f.options || [],
      // Default the client org set for this field (backend pre-fills it into `value`).
      orgDefault: f.orgDefault ?? f.value ?? ''
    }))
  };
};

/** Subtotal / tax / total for one customer's staged services. */
export const customerTotals = (customer) => {
  const services = customer?.services || [];
  const subtotal = services.reduce((sum, s) =>
    sum + (s.service_charge || 0) + (s.govt_charge || 0) + (s.markup_amount || 0), 0);
  const taxTotal = services.reduce((sum, s) => sum + (s.tax_value || 0) + (s.tax_on_markup || 0), 0);
  return { subtotal, taxTotal, grandTotal: subtotal + taxTotal };
};

export const customerHasIdentifier = (c) => Boolean(c?.passportNumber?.trim() || c?.emiratesId?.trim());

/**
 * Validate before submit, as the create page does. Returns { error } for a hard block (missing name,
 * no service, empty required field, missing required document) or {} when OK.
 */
export const validateCustomer = (customer) => {
  if (!customer?.name?.trim()) return { error: 'Please enter the customer name' };
  if (!customer.services?.length) return { error: 'Please add at least one service for the customer' };
  for (const f of mergeCustomFields(customer)) {
    const val = customer.fieldValues?.[f.fieldId];
    if (f.required && (val == null || String(val).trim() === '')) {
      return { error: `Please fill the required field "${f.label}"` };
    }
  }
  const uploaded = new Set((customer.documents || []).map(d => d.documentType));
  const missing = mergeRequiredDocs(customer).filter(r => r.required && !uploaded.has(r.code));
  if (missing.length) {
    return { error: `Required document${missing.length > 1 ? 's' : ''} missing: ${missing.map(m => m.label).join(', ')}` };
  }
  return {};
};

/** The addCustomer request body: the applicant (with the picked Thaklis employee) + one line item per staged service. */
export const buildAddCustomerPayload = (customer) => ({
  customer: {
    name: customer.name.trim(),
    passport_number: customer.passportNumber?.trim() || null,
    emirates_id: customer.emiratesId?.trim() || null,
    employee_uid: customer.employee?.uid || null
  },
  line_items: customer.services.map(item => ({
    service_id: item.service_id && UUID_RE.test(item.service_id) ? item.service_id : null,
    service_name: item.service_name,
    service_description: item.service_description || '',
    service_type: item.service_type || 'INTERNAL',
    quantity: item.quantity,
    unit_price: item.unit_price,
    service_charge: item.service_charge,
    govt_charge: item.govt_charge,
    tax_value: item.tax_value,
    tax_on_markup: item.tax_on_markup,
    line_total: item.line_total,
    original_line_total: item.original_line_total,
    markup_amount: item.markup_amount,
    is_custom_service: item.is_custom_service,
    // The customer's shared field values, distributed to this line's own service fields.
    field_values: (item.customFields || [])
      .map(f => ({ fieldId: f.fieldId, value: customer.fieldValues?.[f.fieldId] }))
      .filter(fv => fv.value != null && String(fv.value).trim() !== '')
  }))
});

/**
 * Upload the customer's staged documents to the work order record, tagged to the customer, with the type code (a
 * client's company document is stored in its documents first) and, for an employee document, the picked employee.
 * Returns the ids of documents that failed, so they are not pushed to Thaklis either.
 */
export const uploadCustomerDocuments = async (workOrderId, customer, documentTypes = []) =>
  // pre-filled company files are attached by key; pre-filled employee files go to the work order only
  submitWorkOrderDocuments({
    workOrderId,
    docs: customer.documents,
    customerName: customer.name?.trim() || null,
    employeeUid: customer.employee?.uid || null,
    documentTypes,
  });

/**
 * Attach the customer's employee documents to their Thaklis employee: the one picked (or Quick-added) on the form, as
 * on work order create; without one (employee selection off), create/reuse it under the mapped org by passport /
 * Emirates ID. No-op without a document, or without an employee and an identifier. Throws if the employee can't be
 * created; individual document failures are logged and skipped.
 */
export const syncCustomerEmployee = async ({ customer, customerType, mappedOrganizationId, internalOrganizationId, failedDocIds = new Set(), documentTypes = [] }) => {
  // new employee-type files only: pre-filled ones came from Thaklis, company documents are not the employee's
  const docs = (customer.documents || []).filter(d => !failedDocIds.has(d.id) && needsEmployeeSync(documentTypes, d));
  const picked = customer.employee?.uid ? customer.employee : null;
  if (!mappedOrganizationId || docs.length === 0 || (!picked && !customerHasIdentifier(customer))) return;

  let result;
  if (picked) {
    // Thaklis keeps the files under the customer's segment: the client's internal org id, or walkin
    result = { uid: picked.uid, segment: picked.segment || internalOrganizationId || 'walkin' };
  } else {
    const resp = await ApiService.createOrReuseEmployee({
      customer_type: customerType,
      mapped_organization_id: mappedOrganizationId,
      internal_organization_id: internalOrganizationId,
      name: customer.name?.trim() || '',
      passport_number: customer.passportNumber?.trim() || null,
      emirates_id: customer.emiratesId?.trim() || null,
      phone: customer.phone?.trim() || null,
      email: customer.email?.trim() || null
    });
    result = resp?.data?.data;
    if (!result?.uid) throw new Error(resp?.data?.error || 'No employee uid returned');
  }

  // Files of the same type are attached together in one batch (all become the latest version).
  const groups = new Map();
  docs.forEach(d => {
    if (!groups.has(d.documentType)) groups.set(d.documentType, []);
    groups.get(d.documentType).push(d);
  });
  await Promise.all(Array.from(groups.entries()).map(async ([documentType, group]) => {
    const pick = (key) => group.find(d => d[key])?.[key] || null;
    try {
      await ApiService.uploadEmployeeDocuments(result.uid, group.map(d => d.file), documentType, {
        documentNumber: pick('documentNumber'),
        issueDate: pick('issueDate'),
        expiryDate: pick('expiryDate')
      }, result.segment);
    } catch (err) {
      console.error(`Failed to sync ${documentType} documents for ${customer.name}:`, err);
    }
  }));
};
